import React, { useState, useEffect } from 'react';
import JobCard from '../components/JobCard';
import {
  FunnelIcon,
  BriefcaseIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FaRegHandshake, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FaPeopleGroup } from 'react-icons/fa6';
import { PiSteeringWheel, PiMoneyWavy } from 'react-icons/pi';
import { BsShopWindow } from 'react-icons/bs';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

// Importar estilos de Swiper
import 'swiper/css';
import 'swiper/css/navigation';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const generateSignature = (apiSecret, method, path, timestamp) => {
  const payload = timestamp + method + path; // Usando Web Crypto API para generar HMAC-SHA256

  const encoder = new TextEncoder();
  const key = encoder.encode(apiSecret);
  const message = encoder.encode(payload);

  return crypto.subtle
    .importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
    .then((cryptoKey) => {
      return crypto.subtle.sign('HMAC', cryptoKey, message);
    })
    .then((signature) => {
      // Convertir ArrayBuffer a hex string
      return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    });
};

const JobListing = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    type: '',
    area: '',
  });
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
  const [areas, setAreas] = useState([]);
  const [hasFiltered, setHasFiltered] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);

  // Eliminamos los estados de paginación
  // const [currentPage, setCurrentPage] = useState(1);
  // const [jobsPerPage] = useState(6);

  // Datos de categorías para el carrusel
  const categories = [
    {
      name: 'Ventas',
      icon: <FaRegHandshake className="h-8 w-8 text-white" />,
      color: {
        bg: 'bg-blue-500',
        bgActive: 'bg-blue-600',
        gradientFrom: 'from-blue-50',
        gradientTo: 'to-blue-100',
        gradientFromActive: 'from-blue-100',
        gradientToActive: 'to-blue-200',
        border: 'border-blue-200',
        borderActive: 'border-blue-300',
        button: 'bg-blue-500',
        buttonActive: 'bg-blue-600',
        buttonHover: 'hover:bg-blue-600',
        buttonActiveHover: 'hover:bg-blue-700',
      },
    },
    {
      name: 'Operaciones',
      icon: <PiSteeringWheel className="h-8 w-8 text-white" />,
      color: {
        bg: 'bg-orange-500',
        bgActive: 'bg-orange-600',
        gradientFrom: 'from-orange-50',
        gradientTo: 'to-orange-100',
        gradientFromActive: 'from-orange-100',
        gradientToActive: 'to-orange-200',
        border: 'border-orange-200',
        borderActive: 'border-orange-300',
        button: 'bg-orange-500',
        buttonActive: 'bg-orange-600',
        buttonHover: 'hover:bg-orange-600',
        buttonActiveHover: 'hover:bg-orange-700',
      },
    },
    {
      name: 'Tesorería',
      icon: <PiMoneyWavy className="h-8 w-8 text-white" />,
      color: {
        bg: 'bg-green-500',
        bgActive: 'bg-green-600',
        gradientFrom: 'from-green-50',
        gradientTo: 'to-green-100',
        gradientFromActive: 'from-green-100',
        gradientToActive: 'to-green-200',
        border: 'border-green-200',
        borderActive: 'border-green-300',
        button: 'bg-green-500',
        buttonActive: 'bg-green-600',
        buttonHover: 'hover:bg-green-600',
        buttonActiveHover: 'hover:bg-green-700',
      },
    },
    {
      name: 'Atracción de Talento',
      icon: <FaPeopleGroup className="h-8 w-8 text-white" />,
      color: {
        bg: 'bg-purple-500',
        bgActive: 'bg-purple-600',
        gradientFrom: 'from-purple-50',
        gradientTo: 'to-purple-100',
        gradientFromActive: 'from-purple-100',
        gradientToActive: 'to-purple-200',
        border: 'border-purple-200',
        borderActive: 'border-purple-300',
        button: 'bg-purple-500',
        buttonActive: 'bg-purple-600',
        buttonHover: 'hover:bg-purple-600',
        buttonActiveHover: 'hover:bg-purple-700',
      },
    },
    {
      name: 'Comercial',
      icon: <BsShopWindow className="h-8 w-8 text-white" />,
      color: {
        bg: 'bg-teal-500',
        bgActive: 'bg-teal-600',
        gradientFrom: 'from-teal-50',
        gradientTo: 'to-teal-100',
        gradientFromActive: 'from-teal-100',
        gradientToActive: 'to-teal-200',
        border: 'border-teal-200',
        borderActive: 'border-teal-300',
        button: 'bg-teal-500',
        buttonActive: 'bg-teal-600',
        buttonHover: 'hover:bg-teal-600',
        buttonActiveHover: 'hover:bg-teal-700',
      },
    },
  ];

  // Función para consolidar áreas bilingües
  const consolidateArea = (area) => {
    if (!area) return area;

    const areaLower = area.toLowerCase().trim();

    // Consolidar Ventas/Sales
    if (areaLower === 'sales' || areaLower === 'ventas') {
      return 'Ventas';
    }

    // Consolidar Operations/Operaciones
    if (areaLower === 'operations' || areaLower === 'operaciones') {
      return 'Operaciones';
    }

    // Consolidar Tesorería
    if (areaLower === 'tesoreria' || areaLower === 'tesorería') {
      return 'Tesorería';
    }

    // Consolidar Atracción de Talento/Recruitment
    if (
      areaLower === 'recruitment' ||
      areaLower === 'atracción de talento' ||
      areaLower === 'atraccion de talento'
    ) {
      return 'Atracción de Talento';
    }

    // Consolidar Comercial/Commercial
    if (areaLower === 'commercial' || areaLower === 'comercial') {
      return 'Comercial';
    }

    // Mantener el nombre original pero con formato consistente
    return area.charAt(0).toUpperCase() + area.slice(1).toUpperCase();
  };

  // Función para verificar si una vacante ha vencido
  const isJobExpired = (job) => {
    if (!job.finaliza_vacante) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalizar a inicio del día

    const expirationDate = new Date(job.finaliza_vacante);
    expirationDate.setHours(0, 0, 0, 0); // Normalizar a inicio del día

    return today > expirationDate;
  };

  // Función para verificar si una vacante está activa
  const isJobActive = (job) => {
    const statusField = job.status || job.estado_vacante || job.activo;
    return (
      statusField === 'activo' ||
      statusField === 'active' ||
      statusField === true
    );
  };

  // Función para obtener solo vacantes disponibles (activas y no vencidas)
  const getAvailableJobs = (jobsList) => {
    return jobsList.filter((job) => isJobActive(job) && !isJobExpired(job));
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);

        const timestamp = Math.floor(Date.now() / 1000).toString();

        const signatureJobListing = await generateSignature(
          process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
          'GET', // Ejemplo: 'GET'
          `/vacantes/`, // Ejemplo: '/categories/with_counts/'
          timestamp
        );

        const response = await fetch(`${API_BASE_URL}/vacantes/`, {
          headers: {
            'X-Timestamp': timestamp,
            'X-Signature': signatureJobListing,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        });
        if (!response.ok) throw new Error('Error al cargar las vacantes');
        const data = await response.json();

        // Filtrar solo las vacantes disponibles (activas y no vencidas)
        const availableJobs = getAvailableJobs(data);

        // Consolidar áreas en los trabajos
        const jobsWithConsolidatedAreas = availableJobs.map((job) => ({
          ...job,
          area_consolidada: consolidateArea(job.area_departamento),
        }));

        setJobs(jobsWithConsolidatedAreas);

        // Extraer áreas únicas consolidadas de los trabajos disponibles
        const uniqueAreas = [
          ...new Set(
            jobsWithConsolidatedAreas.map((job) => job.area_consolidada)
          ),
        ].filter(Boolean);
        setAreas(uniqueAreas);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        // Mock data para desarrollo
        const mockJobs = [
          {
            id: 1,
            titulo_vacante: 'Ejecutivo de Ventas Bilingüe',
            area_departamento: 'Ventas',
            nombre_empresa: 'Packsys',
            localidad: 'Ciudad de México',
            estado: 'CDMX',
            salario: '25000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Buscamos Ejecutivo de Ventas Bilingüe para nuestro equipo dinámico.',
            fecha_posteo: '2024-11-26',
            finaliza_vacante: '2024-12-31', // Fecha futura
            horario: '9:00 - 18:00',
            grado_estudios: 'Licenciatura',
            idioma_vacante: 'Inglés',
            status: 'activo',
          },
          {
            id: 2,
            titulo_vacante: 'Sales Executive',
            area_departamento: 'Sales',
            nombre_empresa: 'Packsys',
            localidad: 'Guadalajara',
            estado: 'Jalisco',
            salario: '30000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Supervisión de procesos operativos y logística.',
            fecha_posteo: '2024-11-25',
            finaliza_vacante: '2024-12-15', // Fecha futura
            horario: '8:00 - 17:00',
            grado_estudios: 'Licenciatura',
            idioma_vacante: 'Español',
            status: 'activo',
          },
          {
            id: 3,
            titulo_vacante: 'Operations Supervisor',
            area_departamento: 'Operations',
            nombre_empresa: 'Packsys',
            localidad: 'Monterrey',
            estado: 'Nuevo León',
            salario: '28000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Gestión y organización de inventario en almacén.',
            fecha_posteo: '2024-11-24',
            finaliza_vacante: '2024-12-20', // Fecha futura
            horario: '7:00 - 16:00',
            grado_estudios: 'Preparatoria',
            idioma_vacante: 'Inglés',
            status: 'activo',
          },
          {
            id: 4,
            titulo_vacante: 'Supervisor de Operaciones',
            area_departamento: 'Operaciones',
            nombre_empresa: 'Packsys',
            localidad: 'Ciudad de México',
            estado: 'CDMX',
            salario: '32000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Liderazgo de equipo comercial y estrategias de venta.',
            fecha_posteo: '2024-11-23',
            finaliza_vacante: '2024-12-25', // Fecha futura
            horario: '9:00 - 18:00',
            grado_estudios: 'Licenciatura',
            idioma_vacante: 'Español',
            status: 'activo',
          },
          {
            id: 5,
            titulo_vacante: 'Warehouse Manager',
            area_departamento: 'Warehouse',
            nombre_empresa: 'Packsys',
            localidad: 'Monterrey',
            estado: 'Nuevo León',
            salario: '22000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Gestión y organización de inventario en almacén.',
            fecha_posteo: '2024-11-22',
            finaliza_vacante: '2024-12-10', // Fecha futura
            horario: '7:00 - 16:00',
            grado_estudios: 'Preparatoria',
            idioma_vacante: 'Inglés',
            status: 'activo',
          },
          {
            id: 6,
            titulo_vacante: 'Encargado de Almacén',
            area_departamento: 'Almacén',
            nombre_empresa: 'Packsys',
            localidad: 'Guadalajara',
            estado: 'Jalisco',
            salario: '20000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Gestión y organización de inventario en almacén.',
            fecha_posteo: '2024-11-21',
            finaliza_vacante: '2024-12-05', // Fecha futura
            horario: '7:00 - 16:00',
            grado_estudios: 'Preparatoria',
            idioma_vacante: 'Español',
            status: 'activo',
          },
          {
            id: 7,
            titulo_vacante: 'Senior Sales Executive',
            area_departamento: 'Sales',
            nombre_empresa: 'Packsys',
            localidad: 'Ciudad de México',
            estado: 'CDMX',
            salario: '45000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta:
              'Experienced sales executive for international clients.',
            fecha_posteo: '2024-11-20',
            finaliza_vacante: '2024-12-30', // Fecha futura
            horario: '9:00 - 18:00',
            grado_estudios: 'Licenciatura',
            idioma_vacante: 'Inglés',
            status: 'activo',
          },
          {
            id: 8,
            titulo_vacante: 'Operations Coordinator',
            area_departamento: 'Operations',
            nombre_empresa: 'Packsys',
            localidad: 'Guadalajara',
            estado: 'Jalisco',
            salario: '26000',
            moneda: 'MXN',
            tipo_empleo: 'Tiempo completo',
            descripcion_corta: 'Coordination of operational processes.',
            fecha_posteo: '2024-11-19',
            finaliza_vacante: '2024-12-18', // Fecha futura
            horario: '8:00 - 17:00',
            grado_estudios: 'Licenciatura',
            idioma_vacante: 'Inglés',
            status: 'activo',
          },
        ];

        // Filtrar solo las vacantes disponibles en los mock data
        const availableMockJobs = getAvailableJobs(mockJobs);

        // Consolidar áreas en los mock jobs
        const mockJobsWithConsolidatedAreas = availableMockJobs.map((job) => ({
          ...job,
          area_consolidada: consolidateArea(job.area_departamento),
        }));

        setJobs(mockJobsWithConsolidatedAreas);

        const mockAreas = [
          ...new Set(
            mockJobsWithConsolidatedAreas.map((job) => job.area_consolidada)
          ),
        ];
        setAreas(mockAreas);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Update active filters
  useEffect(() => {
    const newActiveFilters = [];
    if (filters.search) newActiveFilters.push(`Búsqueda: ${filters.search}`);
    if (filters.location)
      newActiveFilters.push(`Ubicación: ${filters.location}`);
    if (filters.type) newActiveFilters.push(`Tipo: ${filters.type}`);
    if (filters.area) newActiveFilters.push(`Área: ${filters.area}`);
    setActiveFilters(newActiveFilters);

    // Verificar si hay algún filtro activo
    const hasActiveFilter =
      filters.search || filters.location || filters.type || filters.area;
    if (hasActiveFilter && !hasFiltered) {
      setHasFiltered(true);
    }
  }, [filters, hasFiltered]);

  const applyCategoryFilter = (area) => {
    setFilters({
      search: '',
      location: '',
      type: '',
      area: area,
    });
    setHasFiltered(true);
    // setCurrentPage(1); // Eliminado - ya no hay paginación
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    // setCurrentPage(1); // Eliminado - ya no hay paginación
  };

  // Filter and sort jobs - USAR area_consolidada para filtros
  const filteredAndSortedJobs = jobs
    .filter((job) => {
      return (
        (!filters.search ||
          job.titulo_vacante
            .toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          job.descripcion_corta
            .toLowerCase()
            .includes(filters.search.toLowerCase())) &&
        (!filters.location ||
          `${job.localidad}, ${job.estado}`
            .toLowerCase()
            .includes(filters.location.toLowerCase())) &&
        (!filters.type || job.tipo_empleo === filters.type) &&
        (!filters.area || job.area_consolidada === filters.area)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.fecha_posteo) - new Date(a.fecha_posteo);
        case 'salary-high':
          return parseInt(b.salario) - parseInt(a.salario);
        case 'salary-low':
          return parseInt(a.salario) - parseInt(b.salario);
        default:
          return 0;
      }
    });

  // Eliminamos toda la lógica de paginación
  // const indexOfLastJob = currentPage * jobsPerPage;
  // const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  // const currentJobs = filteredAndSortedJobs.slice(
  //   indexOfFirstJob,
  //   indexOfLastJob
  // );
  // const totalPages = Math.ceil(filteredAndSortedJobs.length / jobsPerPage);
  // const paginate = (pageNumber) => setCurrentPage(pageNumber);
  // const nextPage = () => {
  //   if (currentPage < totalPages) {
  //     setCurrentPage(currentPage + 1);
  //   }
  // };
  // const prevPage = () => {
  //   if (currentPage > 1) {
  //     setCurrentPage(currentPage - 1);
  //   }
  // };
  // const getPageNumbers = () => { ... };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      type: '',
      area: '',
    });
    setHasFiltered(false);
    // setCurrentPage(1); // Eliminado - ya no hay paginación
  };

  const removeFilter = (filterToRemove) => {
    const filterKey = filterToRemove.split(': ')[0].toLowerCase();
    const newFilters = {
      ...filters,
      [filterKey === 'búsqueda'
        ? 'search'
        : filterKey === 'ubicación'
          ? 'location'
          : filterKey === 'tipo'
            ? 'type'
            : 'area']: '',
    };

    setFilters(newFilters);
    // setCurrentPage(1); // Eliminado - ya no hay paginación

    // Verificar si aún quedan filtros activo
    const hasRemainingFilters =
      newFilters.search ||
      newFilters.location ||
      newFilters.type ||
      newFilters.area;
    if (!hasRemainingFilters) {
      setHasFiltered(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-gray-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-primary-orange border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
          </div>
          <p className="mt-6 text-gray-600 font-medium">Cargando vacantes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Banner - CON ANIMACIÓN */}
      <div className="relative overflow-hidden">
        {/* Gradiente Ocean animado */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-700 via-blue-500 to-indigo-600 animate-gradient-shift"></div>

        {/* Olas animadas */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 transform skew-x-12 animate-wave-right"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-full bg-white/5 transform -skew-x-12 animate-wave-left"></div>

        {/* Brillo animado */}
        <div className="absolute inset-0 animate-shimmer">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12"></div>
        </div>

        <style jsx>{`
          @keyframes gradientShift {
            0%,
            100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @keyframes waveRight {
            0%,
            100% {
              transform: skew-x(12deg) translateX(0);
            }
            50% {
              transform: skew-x(12deg) translateX(20px);
            }
          }

          @keyframes waveLeft {
            0%,
            100% {
              transform: skew-x(-12deg) translateX(0);
            }
            50% {
              transform: skew-x(-12deg) translateX(-20px);
            }
          }

          @keyframes float {
            0%,
            100% {
              transform: translateY(0) translateX(0);
              opacity: 0.8;
            }
            25% {
              transform: translateY(-20px) translateX(10px);
              opacity: 0.6;
            }
            50% {
              transform: translateY(0) translateX(20px);
              opacity: 0.8;
            }
            75% {
              transform: translateY(20px) translateX(10px);
              opacity: 0.6;
            }
          }

          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }

          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradientShift 8s ease-in-out infinite;
          }

          .animate-wave-right {
            animation: waveRight 6s ease-in-out infinite;
          }

          .animate-wave-left {
            animation: waveLeft 7s ease-in-out infinite;
          }

          .animate-float {
            animation-timing-function: ease-in-out;
          }

          .animate-shimmer {
            animation: shimmer 3s ease-in-out infinite;
          }
        `}</style>

        <div className="container relative z-10 mx-auto px-4 py-24">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6 animate-pulse">
              <BriefcaseIcon className="h-5 w-5 mr-2" />
              <span className="font-medium">Oportunidades laborales</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight animate-fade-in-up">
              Encuentra tu próxima
              <span className="block text-orange-300">
                oportunidad profesional
              </span>
            </h1>

            <p
              className="text-xl opacity-90 leading-relaxed animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              Descubre posiciones que se adaptan a tus habilidades y
              aspiraciones
            </p>

            {/* Contador de vacantes disponibles */}
            <div className="mt-8 inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="font-semibold">
                {jobs.length}{' '}
                {jobs.length === 1
                  ? 'vacante disponible'
                  : 'vacantes disponibles'}
              </span>
              <div className="ml-3 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section - Siempre visible */}
      <div className="container mx-auto px-4 -mt-8 relative z-20 mb-12">
        <div className="bg-white rounded-2xl shadow-2xl p-4 border border-gray-100">
          {/* CARRUSEL DE CATEGORÍAS */}
          <h2 className="text-2xl font-bold text-gray-900 text-center">
            Áreas principales
          </h2>

          <div className="relative px-12 pt-4">
            {/* Botón de navegación izquierda */}
            <button
              onClick={() => swiperInstance?.slidePrev()}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
              aria-label="Categorías anteriores"
            >
              <FaChevronLeft className="h-5 w-5 text-gray-700" />
            </button>

            {/* Botón de navegación derecha */}
            <button
              onClick={() => swiperInstance?.slideNext()}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
              aria-label="Siguientes categorías"
            >
              <FaChevronRight className="h-5 w-5 text-gray-700" />
            </button>

            {/* Carrusel Swiper */}
            <Swiper
              modules={[Navigation]}
              spaceBetween={24}
              slidesPerView={1}
              breakpoints={{
                640: {
                  slidesPerView: 2,
                },
                768: {
                  slidesPerView: 3,
                },
              }}
              onSwiper={setSwiperInstance}
              className="py-4"
            >
              {categories.map((category) => {
                const isActive = filters.area === category.name;
                const color = category.color;

                return (
                  <SwiperSlide key={category.name}>
                    <div
                      className={`h-full rounded-xl p-6 border hover:shadow-lg transition-all duration-300 ${
                        isActive
                          ? `bg-gradient-to-br ${color.gradientFromActive} ${color.gradientToActive} ${color.borderActive} shadow-md`
                          : `bg-gradient-to-br ${color.gradientFrom} ${color.gradientTo} ${color.border}`
                      }`}
                    >
                      <div className="flex flex-col items-center text-center h-full">
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                            isActive ? color.bgActive : color.bg
                          }`}
                        >
                          {category.icon}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {category.name}
                        </h3>
                        <div className="mt-auto w-full">
                          <button
                            onClick={() => applyCategoryFilter(category.name)}
                            className={`w-full py-3 text-white font-medium rounded-lg transition-all duration-300 transform hover:-translate-y-1 ${
                              isActive
                                ? `${color.buttonActive} ${color.buttonActiveHover}`
                                : `${color.button} ${color.buttonHover}`
                            }`}
                          >
                            {isActive ? 'Ver vacantes' : 'Ver vacantes'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span className="text-sm text-gray-600 font-medium">
                Filtros activos:
              </span>
              {activeFilters.map((filter, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-orange/10 text-primary-orange rounded-full text-sm font-medium"
                >
                  {filter}
                  <button
                    onClick={() => removeFilter(filter)}
                    className="text-primary-orange hover:text-primary-blue"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-primary-orange font-medium ml-auto"
              >
                Limpiar todos
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters Section - Ahora siempre se muestra la lista de vacantes */}
      <div className="container mx-auto px-4 relative z-20 mb-16">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {hasFiltered
                ? 'Vacantes filtradas'
                : 'Todas las vacantes disponibles'}
            </h2>
            <p className="text-gray-600">
              Mostrando{' '}
              <span className="font-semibold text-primary-blue">
                {filteredAndSortedJobs.length}
              </span>{' '}
              {filteredAndSortedJobs.length === 1
                ? 'vacante encontrada'
                : 'vacantes encontradas'}
            </p>
          </div>

          <div className="mt-4 sm:mt-0 flex items-center gap-3">
            <div className="text-sm text-gray-500">Ordenar por:</div>
            <select
              value={sortBy}
              onChange={handleSortChange}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all duration-300"
            >
              <option value="recent">Recientes primero</option>
              <option value="salary-high">Salario (Mayor a menor)</option>
              <option value="salary-low">Salario (Menor a mayor)</option>
            </select>
          </div>
        </div>

        {/* Jobs List - Ahora muestra todas las vacantes a la vez */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {filteredAndSortedJobs.length > 0 ? (
            filteredAndSortedJobs.map((job) => (
              <div key={job.id} className="h-full">
                {/* Pasar el área consolidada al JobCard si es necesario */}
                <JobCard
                  job={{ ...job, area_departamento: job.area_consolidada }}
                />
              </div>
            ))
          ) : (
            <div className="col-span-2 text-center py-20">
              <div className="mx-auto w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-8">
                <FunnelIcon className="h-16 w-16 text-gray-400" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                No encontramos coincidencias
              </h3>
              <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto leading-relaxed">
                No hay vacantes que coincidan con tus criterios de búsqueda.
                Intenta con otros filtros o revisa todas nuestras oportunidades.
              </p>
              <button
                onClick={clearFilters}
                className="px-8 py-4 bg-gradient-to-r from-primary-blue to-primary-orange text-white font-medium text-lg rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Ver todas las categorías
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobListing;
